import { useAPI } from '@providers/useAPI';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminUserDetailsResultDto } from 'app/API';
import { adminUsersKeys } from 'app/queries/adminUsers/adminUsersKeys';
import { createContext, ReactNode, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { createFetchAdminUserDetailsQueryFn } from '../api/fetchAdminUserDetails';

type TState = {
  user?: AdminUserDetailsResultDto;
  isLoading: boolean;
  fetchData: () => void;
};

const PageContext = createContext<TState>({
  user: undefined,
  isLoading: false,
  fetchData: () => {},
});

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { userId = '' } = useParams<{ userId: string }>();

  const userQuery = useQuery({
    queryKey: adminUsersKeys.detail(userId),
    queryFn: createFetchAdminUserDetailsQueryFn(call),
    enabled: userId !== '',
  });

  const user = userQuery.data?.result;
  const isLoading = userQuery.isLoading;

  const fetchData = () => {
    void queryClient.invalidateQueries({ queryKey: adminUsersKeys.detail(userId) });
  };

  return (
    <PageContext.Provider
      value={{
        user,
        isLoading,
        fetchData,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
